import classNames from "classnames/dedupe";
import {
  Button,
  Divider,
  Flex,
  Icon,
  Popover,
  Text,
  TextOrHTML,
} from "@simplybusiness/mobius";
import { circleQuestion, fireFlameCurved } from "@simplybusiness/icons";
import type { ProdRecommendation } from "../../../pages";
import { PriceBlock } from "../../shared/PriceBlock";
import { CoverageButton } from "../../shared/CoverageButton";
import "./RecommendationCard.css";

type RecommendationCardProps = {
  recommendation: ProdRecommendation;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onOpenDrawer: (id: string, title: string, content: string) => void;
  unavailableMessage?: string; /** When provided, displays an overlay with this message and disables the card. Supports HTML strings. */
};

const DEFAULT_PRICE = {
  premium: 0.0,
  currencySymbol: "$",
  frequency: "month",
  ratePrefix: "From",
  referenceMark: "*",
} as const;

export const RecommendationCard = ({
  recommendation,
  isSelected,
  onSelect,
  onOpenDrawer,
  unavailableMessage,
}: RecommendationCardProps) => {
  const {
    id,
    fieldName,
    socialProofingLabel,
    productDisplayName,
    alsoKnownAsProduct,
    targetAudienceText,
    coverageText,
    coverageLimit,
  } = recommendation;

  const estimatedPrice = recommendation.estimatedPrice || DEFAULT_PRICE;
  const isUnavailable = Boolean(unavailableMessage);

  const cardClasses = classNames("mobius", "mobius-recommendation-card", {
    "--is-selected": isSelected,
    "--is-unavailable": isUnavailable,
    "--has-badge": Boolean(socialProofingLabel),
  });

  const checkboxClasses = classNames("mobius-recommendation-card__checkbox", {
    "--is-selected": isSelected,
  });

  return (
    <div className="mobius mobius-recommendation-card__wrapper">
      {/* Hidden field with "no" value - provides default when checkbox unchecked */}
      <input type="hidden" name={fieldName} value="no" />
      {/* Checkbox with "yes" value - overrides hidden field when checked */}
      <input
        type="checkbox"
        id={id}
        name={fieldName}
        value="yes"
        className="mobius-recommendation-card__input"
        checked={isSelected}
        disabled={isUnavailable}
        onChange={() => onSelect(id)}
      />
      <div className="mobius-recommendation-card__badge-container">
        {socialProofingLabel && (
          <div className="mobius-recommendation-card__badge">
            <Icon
              icon={fireFlameCurved}
              size="xs"
              aria-hidden
              data-testid="mobius-recommendation-card-badge-icon"
              className="mobius-recommendation-card__badge-icon"
            />
            {socialProofingLabel}
          </div>
        )}
      </div>
      <label
        htmlFor={id}
        data-testid="recommendation-card"
        className={cardClasses}
      >
        <div className="mobius-recommendation-card__content">
          <div className="mobius-recommendation-card__header">
            <Text
              elementType="h3"
              className="mobius-recommendation-card__heading"
            >
              {productDisplayName}
            </Text>
            <div
              data-testid="recommendation-card-checkbox"
              className={checkboxClasses}
              aria-hidden
            >
              <span className="mobius-recommendation-card__checkbox-icon" />
            </div>
          </div>
          <div className="mobius-recommendation-card__also-known-as">
            {!!alsoKnownAsProduct && (
              <Text variant="small" elementType="p">
                Also known as {alsoKnownAsProduct}
              </Text>
            )}
          </div>
          <PriceBlock
            pricePrefix={estimatedPrice.ratePrefix}
            priceValue={estimatedPrice.premium}
            currencySymbol={estimatedPrice.currencySymbol}
            frequency={estimatedPrice.frequency}
            referenceMark={estimatedPrice.referenceMark}
            className="mobius-recommendation-card__pricing"
          />
          <div className="mobius-recommendation-card__description">
            {targetAudienceText && <Text>{targetAudienceText}</Text>}
            <Text>{coverageText}</Text>
          </div>
          {coverageLimit.length > 0 && (
            <Flex
              gap="xxs"
              className="mobius-recommendation-card__limits-wrapper"
            >
              <Text
                elementType="p"
                variant="small"
                className="mobius-recommendation-card__limits"
              >
                <TextOrHTML text={`${coverageLimit[0]}<sup>†</sup>`} />
              </Text>
              {coverageLimit.length > 1 && (
                <Popover
                  className="mobius-recommendation-card__limits-popover"
                  trigger={
                    <Button
                      variant="link"
                      aria-label="Show more coverage limits"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <Icon icon={circleQuestion} size="xs" />
                    </Button>
                  }
                >
                  <div className="mobius-recommendation-card__limits-popover-content">
                    {coverageLimit.slice(1).map((limit, index) => (
                      <TextOrHTML key={index} text={`${limit}<sup>†</sup>`} />
                    ))}
                  </div>
                </Popover>
              )}
            </Flex>
          )}
          <Divider />
          <CoverageButton
            productCode={recommendation.insuranceAdvisorProductCode}
            className="mobius-recommendation-card__cta"
            onClick={e => {
              e.preventDefault();
              onOpenDrawer(id, productDisplayName, recommendation.productInfo);
            }}
          />
        </div>
        {isUnavailable && (
          <div className="mobius-recommendation-card__overlay">
            <TextOrHTML
              text={unavailableMessage as string}
              htmlElementType="div"
              htmlClassName="mobius-recommendation-card__overlay-message"
            />
          </div>
        )}
      </label>
    </div>
  );
};

RecommendationCard.displayName = "RecommendationCard";
